import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiAlertTriangle } from 'react-icons/fi';
import { AUTH_ACTION_TYPES } from '../redux/reducers/authReducer';
import { EMAIL_VERIF_API } from '../config/qbase';
import { emailVerifFetch } from '../services/qbaseClient';
import './NicknameChangeModal.css';

const NicknameChangeModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [newNickname, setNewNickname] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        if (isOpen && user?.nickname) {
            // 모달이 열릴 때 현재 닉네임을 기본값으로 설정
            setNewNickname(user.nickname);
        } else if (!isOpen) {
            // 모달이 닫힐 때 상태 초기화
            setNewNickname('');
            setError('');
            setSuccess('');
        }
    }, [isOpen, user]);

    const handleInputChange = (e) => {
        const { value } = e.target;
        setNewNickname(value);
        
        // 입력 시 에러 메시지 초기화
        if (error) {
            setError('');
        }
        if (success) {
            setSuccess('');
        }
    };

    const validateNickname = () => {
        if (!newNickname.trim()) {
            setError(t('auth:nick.err.required'));
            return false;
        }
        
        if (newNickname.trim().length < 2) {
            setError(t('auth:nick.err.tooShort'));
            return false;
        }
        
        if (newNickname.trim().length > 20) {
            setError(t('auth:nick.err.tooLong'));
            return false;
        }
        
        if (newNickname.trim() === user?.nickname) {
            setError(t('auth:nick.err.same'));
            return false;
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateNickname()) {
            return;
        }
        
        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            const response = await emailVerifFetch(EMAIL_VERIF_API.changeNickname, {
                email: user.email,
                newNickname: newNickname.trim()
            });

            const result = await response.json();

            if (response.ok && result.success) {
                console.log('✅ 닉네임 변경 성공:', result);

                // 테넌트 계정 반영은 **서버가 한다** (EmailVerifService.syncTenantNickname).
                // 공통 저장소를 바꾸는 흐름은 서버가 테넌트도 맞춘다 — 화면마다 챙기지 않는다.
                setSuccess(t('auth:nick.ok', { nickname: result.nickname }));
                
                // Redux 스토어 업데이트
                dispatch({
                    type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
                    payload: {
                        user: {
                            ...user,
                            nickname: result.nickname
                        },
                        isTemporaryPassword: false
                    }
                });
                
                // 로컬스토리지 업데이트
                const updatedUser = {
                    ...user,
                    nickname: result.nickname
                };
                localStorage.setItem('livesql_user', JSON.stringify(updatedUser));
                
                // 2초 후 모달 닫기
                setTimeout(() => {
                    onClose();
                }, 2000);
                
            } else {
                console.error('❌ 닉네임 변경 실패:', result);
                
                if (response.status === 409) {
                    setError(t('auth:nick.err.taken'));
                } else {
                    setError(result.error || result.message || t('auth:nick.err.failed'));
                }
            }
        } catch (error) {
            console.error('닉네임 변경 오류:', error);
            setError(t('auth:nick.err.unexpected'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return; // 로딩 중일 때는 닫기 방지
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="nickname-modal-overlay">
            <div className="nickname-modal">
                <div className="nickname-modal-header">
                    <h2 className="nickname-modal-title">{t('auth:nick.title')}</h2>
                    <button
                        type="button"
                        className="nickname-modal-close"
                        onClick={handleClose}
                        disabled={loading}
                        aria-label={t('auth:nick.close')}
                    >
                        ×
                    </button>
                </div>

                <form className="nickname-modal-form" onSubmit={handleSubmit}>
                    <div className="current-nickname">
                        <span className="current-nickname-label">{t('auth:nick.current')}</span>
                        <span className="current-nickname-value">{user?.nickname || t('auth:nick.none')}</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="newNickname" className="form-label">
                            {t('auth:nick.new')}
                        </label>
                        <input
                            type="text"
                            id="newNickname"
                            name="newNickname"
                            value={newNickname}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder={t('auth:nick.newPlaceholder')}
                            disabled={loading}
                            required
                            minLength={2}
                            maxLength={20}
                        />
                        <div className="nickname-hint">
                            <small>{t('auth:nick.lengthHint')}</small>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            <div className="error-icon"><FiAlertTriangle size={14} /></div>
                            <div className="error-text">{error}</div>
                        </div>
                    )}

                    {success && (
                        <div className="success-message">
                            <div className="success-icon">✅</div>
                            <div className="success-text">{success}</div>
                        </div>
                    )}

                    <div className="nickname-modal-buttons">
                        <button
                            type="submit"
                            className="nickname-button primary"
                            disabled={loading || !newNickname.trim() || newNickname.trim() === user?.nickname}
                        >
                            {loading ? t('auth:nick.changing') : t('auth:nick.title')}
                        </button>
                        
                        <button
                            type="button"
                            className="nickname-button secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            {t('auth:nick.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NicknameChangeModal;